/**
 * usePerBrandHead — hooks the document <head> to the current brand.
 * ─────────────────────────────────────────────────────────────────
 * Feb 27 2026 · Michael asked for Soulumo and OML to be ring-fenced
 * from Reclaim visually. This hook swaps the apple-touch-icon,
 * favicon, PWA manifest, page title and theme-color on every route
 * change so:
 *   • Users who "Add to Home Screen" on soulumo.app get the golden
 *     orb icon labelled "Soulumo"
 *   • Users who do the same on onemillionlights.org get the amber
 *     light icon labelled "OML"
 *   • reclaim.app keeps the existing green-leaf icon labelled
 *     "Reclaim"
 *
 * Pure DOM mutation — no server changes, no separate builds.
 * ────────────────────────────────────────────────────────────── */
import { useEffect } from "react";
import { resolveBrand } from "./lib/brand";

const BRANDS = {
  reclaim: {
    title:       "Reclaim",
    description: "Reclaim – School of Consciousness. Transform through daily practice.",
    icon180:     "/apple-touch-icon-180.png",
    icon192:     "/apple-touch-icon.png",
    manifest:    "/manifest.json",
    themeColor:  "#3D5A34",
  },
  soulumo: {
    title:       "Soulumo",
    description: "Soulumo — installations for the moments that matter.",
    icon180:     "/brand-icons/soulumo-180.png",
    icon192:     "/brand-icons/soulumo-192.png",
    manifest:    "/manifest.soulumo.json",
    themeColor:  "#0b1428",
  },
  oml: {
    title:       "One Million Lights",
    description: "One Million Lights — a global thank-you to the people quietly holding the world together.",
    icon180:     "/brand-icons/oml-180.png",
    icon192:     "/brand-icons/oml-192.png",
    manifest:    "/manifest.oml.json",
    themeColor:  "#0b1428",
  },
};

function upsertLink(rel, href, extra = {}) {
  const selector = extra.sizes
    ? `link[rel="${rel}"][sizes="${extra.sizes}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

function upsertMeta(name, content) {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function usePerBrandHead() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const brand = resolveBrand();
    const cfg = BRANDS[brand] || BRANDS.reclaim;

    // Title + description
    document.title = cfg.title;
    upsertMeta("description", cfg.description);
    upsertMeta("theme-color", cfg.themeColor);
    upsertMeta("apple-mobile-web-app-title", cfg.title);

    // Apple touch icon (iOS home screen)
    upsertLink("apple-touch-icon", cfg.icon180);
    // Some iOS versions honour sizes attribute for higher-density lookup
    upsertLink("apple-touch-icon", cfg.icon180, { sizes: "180x180" });

    // PWA manifest
    upsertLink("manifest", cfg.manifest);

    // Favicon (browser tab)
    upsertLink("icon", cfg.icon192, { type: "image/png" });
  }, []);
}
